import { useMemo, useState } from "react";
import { When } from "react-if";
import { IEmailSendTimeCell } from "shared";

import { ComboBox } from "../../../common/Atoms/ComboBox";
import { useTimezoneList } from "../../hooks/useTimezoneList";

import { formatPct, rate } from "./stats";

export const DAYS = [`Mon`, `Tue`, `Wed`, `Thu`, `Fri`, `Sat`, `Sun`];
/** Cells with fewer sends than this are too noisy to colour */
const MIN_SENDS = 5;

function hourLabel(hour: number) {
  const h12 = hour % 12 === 0 ? 12 : hour % 12;

  return `${h12}${hour < 12 ? `am` : `pm`}`;
}

interface IBucket {
  label: string;
  hours: number[];
}

// Two-hour buckets across the working day, plus one for everything else (8pm-6am)
export const BUCKETS: IBucket[] = [6, 8, 10, 12, 14, 16, 18]
  .map(hour => ({ label: hourLabel(hour), hours: [hour, hour + 1] }))
  .concat({ label: `Other`, hours: [20, 21, 22, 23, 0, 1, 2, 3, 4, 5] });

/** Placeholder rows the org timezone list adds that aren't real zones */
const NOT_ZONES = new Set([`NOT_SYNCED`, `UNKNOWN`]);

export interface IHeatmapCell {
  title: string;
  label: string;
  /** Null when there isn't enough data to shade the cell */
  value: number | null;
}

/** Day x bucket grid, shaded from min (lightest) to max (darkest) */
export function HeatmapGrid({ rows, min, max }: { rows: { day: string; cells: IHeatmapCell[] }[]; min: number; max: number }) {
  const span = Math.max(max - min, 1);

  const cellStyle = (cell: IHeatmapCell) => {
    if (cell.value === null) {
      return { background: `var(--color-gray-50)`, color: `var(--color-gray-400)` };
    }

    const t = Math.round(((cell.value - min) / span) * 100);

    return {
      background: `color-mix(in oklch, var(--color-paidniceGreen-700) ${Math.max(4, t)}%, var(--color-paidniceGreen-50))`,
      color: t > 50 ? `white` : `var(--color-paidniceGreen-800)`,
    };
  };

  return (
    <div className={ `overflow-x-auto` }>
      <div className={ `grid min-w-[560px] gap-1` }
        style={ { gridTemplateColumns: `48px repeat(${BUCKETS.length}, minmax(64px, 1fr))` } }>
        <div />
        { BUCKETS.map(bucket => (
          <div key={ bucket.label }
            className={ `pb-1 text-center text-xs text-gray-500` }>{ bucket.label }</div>
        )) }
        { rows.map(row => (
          <>
            <div key={ row.day }
              className={ `flex items-center text-xs text-gray-500` }>{ row.day }</div>
            { row.cells.map((cell, i) => (
              <div key={ `${row.day}-${i}` }
                title={ cell.title }
                className={ `flex h-9 items-center justify-center rounded text-xs tabular-nums` }
                style={ cellStyle(cell) }>
                { cell.label }
              </div>
            )) }
          </>
        )) }
      </div>
    </div>
  );
}

export function HeatmapLegend({ min, max }: { min: number; max: number }) {
  return (
    <div className={ `flex items-center gap-2 text-xs text-gray-500` }>
      <span>{ formatPct(min, 0) }</span>
      <div className={ `h-2 w-[120px] rounded-full` }
        style={ { background: `linear-gradient(90deg, var(--color-paidniceGreen-50), var(--color-paidniceGreen-700))` } } />
      <span>{ formatPct(max, 0) }</span>
    </div>
  );
}

interface IProps {
  sendTimes: IEmailSendTimeCell[];
  /** Zone the cells were bucketed in */
  timezone: string;
  onTimezoneChange: (timezone: string) => void;
}

export function SendTimeHeatmap({ sendTimes, timezone, onTimezoneChange }: IProps) {
  const [tzSearch, setTzSearch] = useState(``);
  const { filteredOptions } = useTimezoneList(tzSearch);
  const tzOptions = useMemo(() => filteredOptions.filter(o => !NOT_ZONES.has(o.value)), [filteredOptions]);

  const { rows, best, min, max } = useMemo(() => {
    let best: { day: string; bucket: string; openRate: number } | null = null;

    let min = 100;
    let max = 0;

    const rows = DAYS.map((day, dayIdx) => {
      const cells = BUCKETS.map(bucket => {
        const inBucket = sendTimes.filter(c => c.isoDayOfWeek === dayIdx + 1 && bucket.hours.includes(c.hour));
        const sent = inBucket.reduce((s, c) => s + c.sent, 0);
        const opened = inBucket.reduce((s, c) => s + c.opened, 0);
        const openRate = sent >= MIN_SENDS ? rate(opened, sent) : null;

        if (openRate !== null) {
          min = Math.min(min, openRate);
          max = Math.max(max, openRate);

          if (!best || openRate > best.openRate) {
            best = { day, bucket: bucket.label, openRate };
          }
        }

        return {
          value: openRate,
          label: openRate === null ? `–` : `${Math.round(openRate)}%`,
          title: openRate === null
            ? `${day} ${bucket.label}: ${sent} sent (not enough to rate)`
            : `${day} ${bucket.label}: ${formatPct(openRate)} opened of ${sent} sent`,
        } as IHeatmapCell;
      });

      return { day, cells };
    });

    return { rows, best, min, max };
  }, [sendTimes]);

  if (!sendTimes.some(c => c.sent > 0)) {
    return null;
  }

  return (
    <div className={ `flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-5` }>
      <div className={ `flex flex-wrap items-end justify-between gap-3` }>
        <div className={ `flex flex-col gap-0.5` }>
          <h2 className={ `text-base font-semibold text-gray-900` }>{ `Open rate by send time` }</h2>
          <div className={ `text-[13px] text-gray-500` }>
            { `All templates. ` }
            <When condition={ !!best }>
              { `Best slot: ` }
              <strong className={ `text-paidniceGreen-700` }>{ `${best?.day} ${best?.bucket} (${formatPct(best?.openRate ?? null)})` }</strong>
            </When>
            <When condition={ !best }>
              { `Not enough sends in any slot to pick a best time yet.` }
            </When>
          </div>
        </div>
        <div className={ `flex flex-wrap items-center gap-4` }>
          <When condition={ !!best }>
            <HeatmapLegend min={ min }
              max={ max } />
          </When>
          <div className={ `w-[260px]` }>
            <ComboBox
              options={ tzOptions }
              selected={ { label: timezone, value: timezone } }
              onSelect={ ({ value }) => onTimezoneChange(value) }
              query={ tzSearch }
              onQueryChange={ e => setTzSearch(e.target.value) }
              placeholder={ `Timezone` }
            />
          </div>
        </div>
      </div>
      <HeatmapGrid rows={ rows }
        min={ min }
        max={ max } />
      <div className={ `text-xs text-gray-400` }>{ `Cells need at least ${MIN_SENDS} sends to be rated. Other = sends between 8pm and 6am.` }</div>
    </div>
  );
}
